<script setup>
import { Head } from '@inertiajs/vue3'
import { computed, onMounted } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  keywords: {
    type: String,
    default: ''
  },
  canonical: {
    type: String,
    default: ''
  },
  image: {
    type: String,
    default: '/images/common/logo.png'
  },
  type: {
    type: String,
    default: 'website'
  },
  publishedTime: {
    type: String,
    default: ''
  },
  modifiedTime: {
    type: String,
    default: ''
  },
  author: {
    type: String,
    default: 'Lexo'
  },
  structuredData: {
    type: Object,
    default: null
  }
})

const siteName = 'Lexo'
const siteUrl = 'https://lexo.si'
const twitterHandle = '@lexo_si'

const fullTitle = computed(() => {
  return props.title === siteName ? siteName : `${props.title}`
})

const canonicalUrl = computed(() => {
  return props.canonical ? `${siteUrl}${props.canonical}` : ''
})

const imageUrl = computed(() => {
  return props.image.startsWith('http') ? props.image : `${siteUrl}${props.image}`
})

const structuredDataJson = computed(() => {
  if (!props.structuredData) return null
  
  return {
    '@context': 'https://schema.org',
    ...props.structuredData
  }
})

// Inject structured data into head
onMounted(() => {
  if (structuredDataJson.value) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredDataJson.value)
    document.head.appendChild(script)
  }
})
</script>

<template>
  <Head :title="fullTitle">
    <!-- Basic Meta Tags -->
    <meta head-key="description" name="description" :content="description" />
    <meta v-if="keywords" head-key="keywords" name="keywords" :content="keywords" />
    <meta name="author" :content="author" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="sl" />
    <meta name="revisit-after" content="7 days" />
    
    <!-- Open Graph Meta Tags -->
    <meta property="og:type" :content="type" />
    <meta property="og:title" :content="fullTitle" />
    <meta property="og:description" :content="description" />
    <meta property="og:image" :content="imageUrl" />
    <meta v-if="canonicalUrl" property="og:url" :content="canonicalUrl" />
    <meta property="og:site_name" :content="siteName" />
    <meta property="og:locale" content="sl_SI" />
    
    <!-- Article specific Open Graph tags -->
    <meta v-if="publishedTime" property="article:published_time" :content="publishedTime" />
    <meta v-if="modifiedTime" property="article:modified_time" :content="modifiedTime" />
    <meta v-if="author" property="article:author" :content="author" />
    
    <!-- Twitter Card Meta Tags -->
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" :content="twitterHandle" />
    <meta name="twitter:creator" :content="twitterHandle" />
    <meta name="twitter:title" :content="fullTitle" />
    <meta name="twitter:description" :content="description" />
    <meta name="twitter:image" :content="imageUrl" />
    
    <!-- Additional SEO Meta Tags -->
    <meta name="theme-color" content="#2563eb" />
    <meta name="msapplication-TileColor" content="#2563eb" />
    <meta name="apple-mobile-web-app-capable" content="yes" />
    <meta name="apple-mobile-web-app-status-bar-style" content="default" />
    <meta name="apple-mobile-web-app-title" :content="siteName" />
    
    <!-- Canonical URL -->
    <link v-if="canonicalUrl" rel="canonical" :href="canonicalUrl" />
  </Head>
</template>
